import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { openid } from '@/core/openid';
import { Revoke } from './revoke';
import { CodedException } from '..';

export interface HttpRequestConfig extends AxiosRequestConfig {
  allowAnonymous?: boolean;
  revokeRetry?: boolean;
}

const getAuthHeaderValue = async () => {
  const token = await openid.token();
  return `Bearer ${token}`;
};

const AUTHORIZATION_HEADER = 'Authorization';

export class Http {
  protected _axios: AxiosInstance;

  public static async getAuthHeader() {
    return {
      key: AUTHORIZATION_HEADER,
      value: await getAuthHeaderValue(),
    };
  }

  constructor() {
    this._axios = axios.create();

    this._axios.interceptors.request.use(async (request: HttpRequestConfig) => {
      if (!request.allowAnonymous) {
        const { key, value } = await Http.getAuthHeader();
        request.headers = request.headers || {};
        request.headers[key] = value;
      }
      return request;
    });

    this._axios.interceptors.response.use(undefined, async (error) => {
      const config = error.config as HttpRequestConfig | undefined;
      if (config && !config.allowAnonymous && Revoke.is(error)) {
        if (config.revokeRetry) {
          throw CodedException.from(error);
        }

        config.revokeRetry = true;
        await openid.renewRevokedToken();
        const { key, value } = await Http.getAuthHeader();
        config.headers = config.headers || {};
        config.headers[key] = value;
        return this._axios.request(config);
      }

      throw CodedException.from(error);
    });
  }
}
